import type { EntradasDoAno } from "@base/contracts";
import { calcularFunil } from "./calculo";

/** Metas de um periodo qualquer do Dashboard, em reais (nao centavos). */
export type MetaDoPeriodo = {
  readonly faturamentoReais: number;
  readonly investimentoReais: number;
  readonly lucroReais: number;
  readonly ticketMedioReais: number | null;
  readonly roas: number | null;
  readonly vendas: number;
  /** Etapas do funil no periodo (proporcionais aos dias, podem ter fracao). */
  readonly funil: {
    readonly gerenciador: number;
    readonly crm: number;
    readonly mql: number;
    readonly agendado: number;
    readonly consulta: number;
    readonly venda: number;
  };
  readonly investimentoCentavos: number;
};

const DIA_MS = 24 * 60 * 60 * 1000;

/**
 * Meta do periodo a partir das metas mensais: cada mes entra na proporcao dos
 * dias dele dentro do periodo (15 dias de um mes de 30 = metade da meta). Sem
 * funil projetado para algum mes do periodo, `null`: meta pela metade enganaria.
 */
export function metaDoPeriodo(
  anos: ReadonlyMap<number, EntradasDoAno>,
  inicio: Date,
  fim: Date,
): MetaDoPeriodo | null {
  let faturamento = 0;
  let investimento = 0;
  let vendas = 0;
  let faturamentoMarketing = 0;
  let leads = 0;
  let mql = 0;
  let agendamentos = 0;
  let consultas = 0;
  const calculados = new Map<number, ReturnType<typeof calcularFunil>>();

  const dia = new Date(Date.UTC(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()));
  const ultimo = Date.UTC(fim.getFullYear(), fim.getMonth(), fim.getDate());
  for (; dia.getTime() <= ultimo; dia.setTime(dia.getTime() + DIA_MS)) {
    const ano = dia.getUTCFullYear();
    const entradas = anos.get(ano);
    if (!entradas) return null;
    const funil = calculados.get(ano) ?? calcularFunil(entradas);
    calculados.set(ano, funil);
    const mes = funil.meses[dia.getUTCMonth()];
    if (!mes) return null;
    const diasNoMes = new Date(Date.UTC(ano, dia.getUTCMonth() + 1, 0)).getUTCDate();
    faturamento += mes.faturamentoTotalCentavos / diasNoMes;
    investimento += mes.orcamentoCentavos / diasNoMes;
    vendas += mes.vendas / diasNoMes;
    faturamentoMarketing += mes.faturamentoMarketingCentavos / diasNoMes;
    leads += mes.leads / diasNoMes;
    mql += mes.mql / diasNoMes;
    agendamentos += mes.agendamentos / diasNoMes;
    consultas += mes.consultas / diasNoMes;
  }

  return {
    faturamentoReais: faturamento / 100,
    investimentoReais: investimento / 100,
    lucroReais: (faturamento - investimento) / 100,
    ticketMedioReais: vendas > 0 ? faturamentoMarketing / vendas / 100 : null,
    roas: investimento > 0 ? faturamentoMarketing / investimento : null,
    vendas,
    // Leads no CRM tem a mesma meta do gerenciador (como na planilha).
    funil: { gerenciador: leads, crm: leads, mql, agendado: agendamentos, consulta: consultas, venda: vendas },
    investimentoCentavos: investimento,
  };
}

const NOME_DO_MES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

/**
 * Meses do periodo sem Funil Projetado: o ano nao foi criado ou o mes esta
 * todo em branco. Ex.: ["outubro/2026"]. Vazio = periodo todo planejado.
 */
export function mesesSemPlanejamento(anos: ReadonlyMap<number, EntradasDoAno>, inicio: Date, fim: Date): string[] {
  const faltando: string[] = [];
  let ano = inicio.getFullYear();
  let mes = inicio.getMonth();
  while (ano < fim.getFullYear() || (ano === fim.getFullYear() && mes <= fim.getMonth())) {
    const entradas = anos.get(ano)?.meses[mes];
    const planejado = entradas !== undefined && Object.values(entradas).some((valor) => valor > 0);
    if (!planejado) faltando.push(`${NOME_DO_MES[mes]}/${ano}`);
    mes += 1;
    if (mes === 12) {
      mes = 0;
      ano += 1;
    }
  }
  return faltando;
}
